import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";
import { Button } from "../../components/ui";
import LinkButton from "../../components/ui/LinkButton";
import { FiUploadCloud } from "react-icons/fi";
import { InlineLoader } from "../../components/Loader";
import RoundedContainer from "../../components/containers/RoundedContainer";
import useExcelUpload from "../../hooks/useExcelUpload";
import useDownloadGetFile from "../../hooks/useDownloadGetFile";
import { useNavigate } from "react-router-dom";

const UploadEmployees = ({ maxRows = 100 }) => {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);

  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const { download: downloadTemplate, isLoading: isDownloadingTemplate } =
    useDownloadGetFile({
      url: "downloadBulkEmployeeExcelTemplate",
      filename: "employee_template.xlsx",
      onSuccess: () => {
        toast.success("Template downloaded successfully!");
      },
      onError: () => {
        toast.error("Failed to download template. Please try again.");
      },
    });

  const { upload, isLoading: isUploading } = useExcelUpload({
    url: "addBulkEmployee",
    paramName: "file",
    onSuccess: (response) => {
      const createdCount = response?.data?.created?.length || 0;
      const updatedCount = response?.data?.updated?.length || 0;

      let successMessage = "";
      if (createdCount > 0 && updatedCount > 0) {
        successMessage = `Successfully created ${createdCount} and updated ${updatedCount} employees.`;
      } else if (createdCount > 0) {
        successMessage = `Successfully created ${createdCount} employee${createdCount === 1 ? "" : "s"}.`;
      } else if (updatedCount > 0) {
        successMessage = `Successfully updated ${updatedCount} employee${updatedCount === 1 ? "" : "s"}.`;
      } else {
        successMessage = response?.data?.message || "Employees uploaded successfully!";
      }

      toast.success(successMessage);
      navigate('/employee-details')
      // setFile(null);
      // setData([]);
      // setHeaders([]);
      // if (fileInputRef.current) fileInputRef.current.value = "";
      // setTimeout(() => navigate('/employee-details'), 1000);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to upload employees file. Please try again.";
      toast.error(errorMessage);
    },
  });

  const handleFileUpload = (files) => {
    const uploadedFile = files?.[0];
    if (!uploadedFile) return;

    const validExtensions = [".xlsx", ".xls"];
    const fileExtension = uploadedFile.name
      .substring(uploadedFile.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast.error("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const bstr = event.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        // Extract headers first to handle empty data scenario
        const sheetDataArray = XLSX.utils.sheet_to_json(ws, { header: 1 });
        let parsedHeaders = [];
        if (sheetDataArray.length > 0) {
          parsedHeaders = sheetDataArray[0].filter(h => h); // Filter out empty cells
        }

        const parsedData = XLSX.utils.sheet_to_json(ws, { defval: "" });

        setHeaders(parsedHeaders);
        setData(parsedData);
      } catch (err) {
        toast.error("An error occurred while parsing the Excel file.");
      }
    };
    reader.onerror = () => {
      toast.error("Failed to read the file.");
    };
    reader.readAsBinaryString(uploadedFile);
  };

  const handleInputChange = (e) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleSubmit = () => {
    if (file) {
      upload.mutate(file);
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate.mutate();
  };

  return (
    <section className="pt-10 pb-10">
      <div className="container mb-4">
        <div className="flex justify-end space-x-2 px-4">
          <Button
            onClick={handleDownloadTemplate}
            variant={"outline"}
            disabled={isDownloadingTemplate}
          >
            {isDownloadingTemplate && <InlineLoader className="text-primary mr-2" />}
            Download Template
          </Button>
          <LinkButton to="/add-employee-details" variant="default">
            Add Single Employee
          </LinkButton>
        </div>
      </div>
      <RoundedContainer className="mx-4">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Upload Employees
            </h2>
            <LinkButton to="/employee-details" variant="outline">
              Back to Employees
            </LinkButton>
          </div>

          <div className="flex flex-col gap-4">
            <div
              className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-10 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={handleButtonClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <FiUploadCloud className="w-12 h-12 text-gray-400 mb-4" />
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleInputChange}
                ref={fileInputRef}
                className="hidden"
                onClick={(e) => e.stopPropagation()}
              />
              <Button onClick={(e) => {
                e.stopPropagation();
                handleButtonClick();
              }} variant="outline">
                Select Excel File
              </Button>
              <p className="text-sm text-gray-500 mt-2">or drag and drop your file here</p>
              {file && (
                <p className="mt-4 text-sm text-green-600 font-medium">
                  Selected File: {file.name}
                </p>
              )}
            </div>
          </div>

          {headers.length > 0 && (
            <div className="flex flex-col gap-4 mt-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Preview Excel File</h3>
                <p className="text-sm text-gray-500 mt-1">Review your data before submitting.</p>
              </div>

              {data.length > maxRows && (
                <div className="bg-red-50 text-red-700 p-4 rounded-md border border-red-200">
                  <p className="font-semibold">Maximum Row Limit Exceeded</p>
                  <p className="text-sm">
                    You have uploaded {data.length} rows, but the maximum allowed is {maxRows} rows. 
                    Please reduce the number of rows and try again.
                  </p>
                </div>
              )}
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border-b border-gray-200 font-semibold text-sm text-primary">
                        Row
                      </th>
                      {headers.map((header) => (
                        <th
                          key={header}
                          className="p-3 border-b border-gray-200 font-semibold text-sm text-primary whitespace-nowrap"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan={headers.length + 1} className="p-5 text-center text-gray-500">
                          No data rows found in the uploaded file.
                        </td>
                      </tr>
                    ) : (
                      data.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={
                            rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="p-3 border-b border-gray-200 text-sm text-gray-500">
                            {rowIndex + 1}
                          </td>
                          {headers.map((header) => (
                            <td
                              key={`${rowIndex}-${header}`}
                              className="p-3 border-b border-gray-200 text-sm whitespace-nowrap text-gray-700"
                            >
                              {row[header] || ""}
                            </td>
                          ))}
                        </tr>
                      )))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleSubmit}
                  disabled={isUploading || !file || data.length === 0 || data.length > maxRows}
                  className="w-40"
                >
                  {isUploading && <InlineLoader className="text-white mr-2" />}
                  {isUploading ? "Uploading..." : "Submit"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </RoundedContainer>
    </section>
  );
};

export default UploadEmployees;
